import React, { useMemo } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

import { useLanguage } from './LanguageContext';

/**
 * RTL/LTR-aware MUI theme + emotion cache. When the user toggles the
 * language to Arabic via LanguageContext, this provider:
 *   - injects the rtl stylis plugin so MUI's emotion-generated CSS flips
 *     margins/paddings/positioning,
 *   - sets `theme.direction = 'rtl'` so MUI components compute their own
 *     positions correctly (drawers, popovers, etc.),
 *   - re-mounts the subtree via a key so stale LTR positions don't linger.
 */
export const RtlProvider: React.FC<{ baseTheme: Theme; children: React.ReactNode }> = ({
  baseTheme,
  children,
}) => {
  const { language } = useLanguage();
  const dir: 'rtl' | 'ltr' = language === 'ar' ? 'rtl' : 'ltr';

  const cache = useMemo(
    () =>
      createCache({
        key: dir === 'rtl' ? 'mui-rtl' : 'mui',
        stylisPlugins: dir === 'rtl' ? [prefixer, rtlPlugin] : [prefixer],
      }),
    [dir],
  );

  // Two-arg createTheme(base, overrides) preserves all of base's computed
  // values; spreading would lose them.
  const theme = useMemo(
    () => createTheme(baseTheme, { direction: dir }),
    [baseTheme, dir],
  );

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <div key={language} dir={dir} style={{ display: 'contents' }}>
          {children}
        </div>
      </ThemeProvider>
    </CacheProvider>
  );
};
